import React, { useRef } from 'react';
import ReactDOM from 'react-dom';
import {App} from "./app";
import {
  saveProjectAs,
  loadProject,
  saveProject,
  newProject
} from "./project-utils";
import {Provider} from 'react-redux';
import {store} from './redux/store';
import {
  getCurrentWindow,
  Menu,
  isDevMode,
  remote,
  fs,
  shell,
  logDirectoryPath
} from './electron-modules';
import {
  WorkflowExecutable,
  PythonExecutable
} from "./executable";
import { SnackbarProvider } from 'notistack';
import { Button } from '@material-ui/core';
import Zoom from '@material-ui/core/Zoom';

const sendToMainWindow = (message, payload=null) => {
  const mainWindow = remote.getCurrentWindow();
  mainWindow.webContents.send(message, payload);
};

const loadLogDirectory = () => {
  fs.promises.mkdir(logDirectoryPath).catch(() => {});
  shell.openPath(logDirectoryPath);
};

const generateTemplateList = () => {
  // Generate menu from templates
  let template = [fileMenu, toolsMenu, helpMenu] as any[];

  if (isDevMode) {
    template.push(debugMenu);
  }

  return template;
};

const fileMenu = {
  label: 'File',
  submenu: [
    {
      label: 'Open Engine Workflow',
      click: async () => {
        new WorkflowExecutable().run({command: []});
      }
    },
    {type: 'separator'},
    {
      label: 'New Project',
      click: async () => {
        newProject();
      }
    },
    {
      label: 'Open Project',
      click: async () => {
        loadProject();
      }
    },
    {
      label: 'Save Project',
      click: async () => {
        saveProject();
      }
    },
    {
      label: 'Save Project as...',
      click: async () => {
        saveProjectAs();
      }
    },
    {type: 'separator'},
    {role: 'quit'}
  ]
};

const helpMenu = {
  label: 'Help',
  submenu: [
    {
      label: 'Open Log Directory',
      click: loadLogDirectory
    }
  ]
};

const toolsMenu = {
  label: 'Tools',
  id: 'tools',
  submenu: [
    {
      id: 'projections',
      label: 'Projection Manager',
      click: async () => {
        sendToMainWindow("open-projection-manager");
      }
    },
    {
      id: 'aligner-advanced-menu',
      label: 'Data Aligner (Advanced)',
      enabled: false,
      click: async () => {
        sendToMainWindow("open-image-aligner-adv");
      }
    },
    {
      id: 'aligner-basic-menu',
      label: 'Data Aligner (Basic)',
      enabled: false,
      click: async () => {
        sendToMainWindow("open-image-aligner-basic");
      }
    },
    {
      id: 'label-ortho-menu',
      label: 'Aerial Classification',
      enabled: false,
      click: async () => {
        sendToMainWindow("open-label-ortho");
      }
    },
    {
      id: 'label-scene-menu',
      label: 'Scene View Classification',
      enabled: false,
      visible: false, // Disabled until we make more progress on the export side
      click: async () => {
        sendToMainWindow("open-label-scene");
      }
    },
    {
      id: 'exporter-menu',
      label: 'Export',
      enabled: false,
      click: async () => {
        sendToMainWindow("open-static-export");
      }
    }
  ]
};

const debugMenu = {
  label: 'Refresh',
  click: () => {
    getCurrentWindow().reload();
  }
};

const setToolAvailability = async toolName => {
  const exe = new PythonExecutable();
  exe.run({
    command: ['--check-tool', toolName],
    showStatus: false,
    onLine: jsonData => {
      Menu.getApplicationMenu().items
        .find(item => item.id === 'tools').submenu.items
        .find(item => item.id === toolName).enabled = jsonData.available;
    }
  });
};

const checkAllowedMenuItems = async () => {
  // TODO: check all tools at once
  setToolAvailability('aligner-advanced-menu');
  setToolAvailability('aligner-basic-menu');
  setToolAvailability('exporter-menu');
  setToolAvailability('label-ortho-menu');
  setToolAvailability('label-scene-menu');
};

if (Menu) {
  const template = generateTemplateList();
  const menu = Menu.buildFromTemplate(template);
  Menu.setApplicationMenu(menu);
  checkAllowedMenuItems();
}

const MessageProvider = (props) => {
  const messageRef = useRef(null);

  const onMessageClose = key => () => {
    messageRef.current.closeSnackbar(key);
  };

  return (
    <SnackbarProvider
      ref={messageRef}
      maxSnack={2}
      TransitionComponent={Zoom}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      action={(key) => (
        <Button size="medium" onClick={onMessageClose(key)}>
          Dismiss
        </Button>
      )}
    >
      {props.children}
    </SnackbarProvider>
  );
};

ReactDOM.render(
  <Provider store={store}>
    <MessageProvider>
      <App/>
    </MessageProvider>
  </Provider>,
  document.getElementById('root')
);